import { useCallback, useEffect, useRef, useState } from 'react';
import { reportGameOver, reportPlay } from './beacon';
import { EtooLogo } from './EtooLogo';
import { SnakeEngine, type HudSnapshot } from './game/engine';
import { bindInput } from './game/input';
import FeedbackForm from './components/FeedbackForm';

const emptyHud: HudSnapshot = {
  score: 0,
  best: 0,
  phase: 'ready',
  boostMs: 0,
  isNewBest: false,
};

export default function App() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const engineRef = useRef<SnakeEngine | null>(null);
  const [hud, setHud] = useState<HudSnapshot>(emptyHud);
  const [showFeedback, setShowFeedback] = useState(false);
  const runStartRef = useRef(0);
  const reportedRef = useRef(true);
  const phaseRef = useRef<HudSnapshot['phase']>('ready');
  phaseRef.current = hud.phase;

  // Close the loop if the tab is shut mid-run (drive/sonic-jet pattern):
  // the game-over screen never fires, so without this abandoned runs vanish.
  useEffect(() => {
    const closeOut = () => {
      if (phaseRef.current === 'playing' && !reportedRef.current && runStartRef.current > 0) {
        reportedRef.current = true;
        try {
          reportGameOver(performance.now() - runStartRef.current);
        } catch {
          /* ignore */
        }
      }
    };
    window.addEventListener('pagehide', closeOut);
    const onVis = () => {
      if (document.visibilityState === 'hidden') closeOut();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('pagehide', closeOut);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const engine = new SnakeEngine(ctx, {
      onHud: (next) => setHud(next),
      onPlay: () => {
        runStartRef.current = performance.now();
        reportedRef.current = false;
        try {
          reportPlay();
        } catch {
          /* ignore */
        }
      },
      onGameOver: (durationMs) => {
        reportedRef.current = true;
        try {
          reportGameOver(durationMs);
        } catch {
          /* ignore */
        }
      },
    });
    engineRef.current = engine;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = window.innerWidth;
      const h = window.innerHeight;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      engine.resize(w, h);
    };
    resize();
    window.addEventListener('resize', resize);
    engine.start();

    const unbind = bindInput(
      canvas,
      (dir) => engine.inputDir(dir),
      () => engine.replay(),
    );

    return () => {
      unbind();
      window.removeEventListener('resize', resize);
      engine.destroy();
      engineRef.current = null;
    };
  }, []);

  const playAgain = useCallback(() => {
    engineRef.current?.replay();
  }, []);

  const boostSec = hud.boostMs > 0 ? Math.ceil(hud.boostMs / 1000) : 0;

  return (
    <div className="fixed inset-0 overflow-hidden bg-[#a8d44a]">
      <canvas
        ref={canvasRef}
        className="absolute inset-0 block h-full w-full touch-none select-none"
        aria-label="Snake game. Use arrow keys, WASD, or swipe to steer."
      />

      <div className="pointer-events-none absolute inset-x-0 top-3 flex justify-center px-24">
        <div className="flex items-center gap-6 rounded-2xl bg-white/85 px-5 py-2 shadow-sm backdrop-blur-sm">
          <div className="text-center">
            <p className="font-body text-[10px] font-extrabold uppercase tracking-[0.18em] text-snake-ink/45">
              Score
            </p>
            <p className="font-display text-2xl font-semibold text-snake-ink">{hud.score}</p>
          </div>
          <div className="h-8 w-px bg-snake-ink/10" />
          <div className="text-center">
            <p className="font-body text-[10px] font-extrabold uppercase tracking-[0.18em] text-snake-ink/45">
              Best
            </p>
            <p className="font-display text-2xl font-semibold text-amber-500">{hud.best}</p>
          </div>
        </div>
      </div>

      <button
        onClick={() => setShowFeedback(true)}
        className="fixed right-3 top-3 z-30 rounded-full border border-white/70 bg-white/90 px-3 py-1.5 text-sm shadow-sm backdrop-blur-sm hover:bg-white"
        title="Send feedback"
        aria-label="Send feedback"
      >
        💬
      </button>

      {/* Boost indicator — stacked below feedback to avoid overlap */}
      {boostSec > 0 && hud.phase === 'playing' && (
        <div className="pointer-events-none absolute right-3 top-14">
          <span className="rounded-full bg-amber-300 px-3 py-1.5 font-display text-sm font-semibold text-snake-ink shadow-sm">
            2× {boostSec}s
          </span>
        </div>
      )}

      {hud.phase === 'ready' && (
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-between pt-[18vh] pb-[10vh]">
          <h1 className="font-display text-5xl font-semibold text-white [text-shadow:0_3px_0_rgba(30,58,95,0.2)] sm:text-6xl">
            SNAKE
          </h1>
          <div className="rounded-2xl bg-white/90 px-6 py-4 text-center shadow-md">
            <p className="font-display text-base font-semibold text-snake-ink">Swipe or use arrows</p>
            <p className="mt-1 font-body text-sm font-semibold text-snake-ink/60">WASD works too</p>
          </div>
        </div>
      )}

      {hud.phase === 'gameover' && (
        <div className="absolute inset-0 flex items-center justify-center bg-[rgba(30,58,95,0.28)]">
          <div className="w-[280px] rounded-3xl bg-white p-6 text-center shadow-xl">
            <h2 className="font-display text-2xl font-semibold tracking-wide text-snake-ink">GAME OVER</h2>
            <p className="mt-5 font-body text-lg font-bold text-snake-ink">
              Score: {hud.score}
            </p>
            <p className="mt-1 font-body text-lg font-bold text-amber-500">Best: {hud.best}</p>
            {hud.isNewBest && (
              <p className="mt-3 rounded-full bg-amber-100 px-3 py-1 font-body text-xs font-extrabold uppercase tracking-widest text-amber-600">
                New best!
              </p>
            )}
            <button
              type="button"
              onClick={playAgain}
              className="mt-6 w-full rounded-2xl bg-[#4d7cff] px-4 py-3.5 font-display text-lg font-semibold text-white shadow-[0_4px_0_#3b66e0] transition hover:brightness-105 active:translate-y-0.5 active:shadow-none"
            >
              PLAY AGAIN
            </button>
          </div>
        </div>
      )}

      <EtooLogo />

      {showFeedback && <FeedbackForm onClose={() => setShowFeedback(false)} />}
    </div>
  );
}
